(() => {
  class CircleCarousel {
    constructor(section) {
      this.section = section;
      this.wrapper = section.querySelector('[data-circle-carousel]');
      this.viewport = section.querySelector('.circle-carousel__viewport');
      this.list = section.querySelector('.circle-carousel__list');
      this.items = Array.from(
        section.querySelectorAll('.circle-carousel__item')
      );

      this.prevButton = section.querySelector(
        '.circle-carousel__arrow--prev'
      );

      this.nextButton = section.querySelector(
        '.circle-carousel__arrow--next'
      );

      if (
        !this.wrapper ||
        !this.viewport ||
        !this.list ||
        !this.items.length
      ) {
        return;
      }

      this.maxItems =
        parseInt(
          this.wrapper.dataset.maxItemsPerPage,
          10
        ) || 8;

      this.autoplay =
        this.wrapper.dataset.autoplay === 'true';

      this.autoplaySpeed =
        parseInt(
          this.wrapper.dataset.autoplaySpeed,
          10
        ) || 6000;

      this.autoplayTimer = null;
      this.resizeTimer = null;

      this.bindEvents();
      this.updateLayout();
      this.updateButtons();

      if (this.autoplay) {
        this.startAutoplay();
      }
    }


    getResponsiveItemCount() {
      const width = window.innerWidth;

      let responsiveCount;

      if (width <= 359) {
        responsiveCount = 2;
      } else if (width <= 479) {
        responsiveCount = 3;
      } else if (width <= 749) {
        responsiveCount = 4;
      } else if (width <= 989) {
        responsiveCount = 5;
      } else if (width <= 1199) {
        responsiveCount = 6;
      } else {
        responsiveCount = this.maxItems;
      }

      return Math.max(
        1,
        Math.min(
          this.maxItems,
          responsiveCount,
          this.items.length
        )
      );
    }


    updateLayout() {
      const visibleItems = this.getResponsiveItemCount();

      this.wrapper.style.setProperty(
        '--items-visible',
        visibleItems
      );

      requestAnimationFrame(() => {
        this.updateButtons();
      });
    }


    getGap() {
      const styles = window.getComputedStyle(this.list);

      return (
        parseFloat(styles.columnGap) ||
        parseFloat(styles.gap) ||
        0
      );
    }


    getStep() {
      const firstItem = this.items[0];

      if (!firstItem) {
        return 0;
      }

      return (
        firstItem.getBoundingClientRect().width +
        this.getGap()
      );
    }


    getMaxScroll() {
      return Math.max(
        0,
        this.viewport.scrollWidth -
          this.viewport.clientWidth
      );
    }


    next() {
      const maxScroll = this.getMaxScroll();

      if (maxScroll <= 2) {
        return;
      }

      const currentScroll = this.viewport.scrollLeft;

      if (currentScroll >= maxScroll - 5) {
        this.viewport.scrollTo({
          left: 0,
          behavior: 'smooth'
        });

        return;
      }

      this.viewport.scrollBy({
        left: this.getStep(),
        behavior: 'smooth'
      });
    }


    previous() {
      const maxScroll = this.getMaxScroll();

      if (maxScroll <= 2) {
        return;
      }

      const currentScroll = this.viewport.scrollLeft;

      if (currentScroll <= 5) {
        this.viewport.scrollTo({
          left: maxScroll,
          behavior: 'smooth'
        });

        return;
      }

      this.viewport.scrollBy({
        left: -this.getStep(),
        behavior: 'smooth'
      });
    }


    updateButtons() {
      const canScroll =
        this.viewport.scrollWidth >
        this.viewport.clientWidth + 2;

      if (this.prevButton) {
        this.prevButton.disabled = !canScroll;
        this.prevButton.style.visibility =
          canScroll ? 'visible' : 'hidden';
      }

      if (this.nextButton) {
        this.nextButton.disabled = !canScroll;
        this.nextButton.style.visibility =
          canScroll ? 'visible' : 'hidden';
      }
    }


    startAutoplay() {
      this.stopAutoplay();

      if (!this.autoplay || this.items.length <= 1) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.next();
      }, this.autoplaySpeed);
    }


    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }


    restartAutoplay() {
      if (!this.autoplay) {
        return;
      }

      this.stopAutoplay();
      this.startAutoplay();
    }


    bindEvents() {
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.next();
          this.restartAutoplay();
        });
      }


      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          this.previous();
          this.restartAutoplay();
        });
      }


      this.viewport.addEventListener(
        'scroll',
        () => {
          window.requestAnimationFrame(() => {
            this.updateButtons();
          });
        },
        {
          passive: true
        }
      );


      this.wrapper.addEventListener(
        'mouseenter',
        () => {
          this.stopAutoplay();
        }
      );


      this.wrapper.addEventListener(
        'mouseleave',
        () => {
          this.startAutoplay();
        }
      );


      this.wrapper.addEventListener(
        'focusin',
        () => {
          this.stopAutoplay();
        }
      );


      this.wrapper.addEventListener(
        'focusout',
        () => {
          this.startAutoplay();
        }
      );


      this.wrapper.addEventListener(
        'touchstart',
        () => {
          this.stopAutoplay();
        },
        {
          passive: true
        }
      );


      this.wrapper.addEventListener(
        'touchend',
        () => {
          window.setTimeout(() => {
            this.startAutoplay();
          }, 1500);
        },
        {
          passive: true
        }
      );


      window.addEventListener(
        'resize',
        () => {
          clearTimeout(this.resizeTimer);

          this.resizeTimer = setTimeout(() => {
            this.updateLayout();
          }, 100);
        },
        {
          passive: true
        }
      );
    }
  }


  const initializeCircleCarousels = (container = document) => {
    const sections = container.querySelectorAll(
      '.circle-carousel[data-section-id]'
    );

    sections.forEach((section) => {
      if (section.dataset.circleCarouselInitialized === 'true') {
        return;
      }

      section.dataset.circleCarouselInitialized = 'true';

      new CircleCarousel(section);
    });
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeCircleCarousels();
      }
    );
  } else {
    initializeCircleCarousels();
  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeCircleCarousels(event.target);
    }
  );
})();